import { Component, OnInit, OnDestroy } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import {
  JobApplication,
  JobApplicationServiceService,
} from '../all-applications/service/job-application-service.service';
import { ActivatedRoute, Router } from '@angular/router';
import { environment } from 'src/environments/environment';
import { GoogleTagManagerService } from 'angular-google-tag-manager';
import { GoogleAnalyticsService } from 'ngx-google-analytics';

@Component({
  selector: 'app-app-details',
  templateUrl: './app-details.component.html',
  styleUrls: ['./app-details.component.css'],
})
export class AppDetailsComponent implements OnInit, OnDestroy {
  applicationId: string | null = null;
  applicationDetails!: JobApplication;
  isLoading: boolean = false;
  errorMessage: string = '';

  hrNote: string = '';
  savedNote: string = '';

  cvBlobUrl: SafeResourceUrl | null = null;
  isCvLoading: boolean = false;
  cvError: boolean = false;

  // Status update state
  isUpdatingStatus: boolean = false;
  updatingStatus: string | null = null;
  isSavingNote: boolean = false;

  // Toast notifications
  showToast: boolean = false;
  toastMessage: string = '';
  toastType: 'success' | 'error' = 'success';

  // Schedule interview confirmation modal
  showConfirmModal: boolean = false;

  cancelScheduleInterview(): void {
    this.showConfirmModal = false;
  }

  confirmScheduleInterview(): void {
    this.showConfirmModal = false;
    this.updateStatus('SELECTED_FOR_INTERVIEW');
  }

  constructor(
    private applicationService: JobApplicationServiceService,
    private route: ActivatedRoute,
    private sanitizer: DomSanitizer,
    private http: HttpClient,
    private gtmService: GoogleTagManagerService,
    private googleAnalyticsService: GoogleAnalyticsService,
    private router: Router,
  ) {
    this.applicationId = this.route.snapshot.paramMap.get('id');
  }

  ngOnInit(): void {
    if (this.applicationId) {
      this.getApplicationDetails(this.applicationId);
    }
  }

  ngOnDestroy(): void {
    if (this.cvBlobUrl) {
      URL.revokeObjectURL(this.cvBlobUrl as string);
    }
  }

  getApplicationDetails(id: string): void {
    this.isLoading = true;
    this.errorMessage = '';

    this.applicationService.getApplicationDetails(id).subscribe({
      next: (res: JobApplication) => {
        this.applicationDetails = res;
        this.isLoading = false;
        // Load any existing notes if available
        if (res.notes) {
          this.hrNote = res.notes;
          this.savedNote = res.notes;
        }
        if (res.cvUrl) {
          this.loadCvBlob(res.cvUrl);
        }
      },
      error: (err) => {
        this.errorMessage =
          'Failed to load application details. Please try again.';
        this.isLoading = false;
        console.error('Error fetching application details:', err);
      },
    });
  }

  onShortlist(): void {
    this.updateStatus('SHORTLISTED');
  }

  onRejectApplication(): void {
    this.updateStatus('REJECTED');
  }

  onAccept(): void {
    this.updateStatus('ACCEPTED');
  }

  // ─── Single source of truth for building the full CV URL ───
  private buildFullCvUrl(cvUrl: string): string {
    if (!cvUrl) return '';
    if (cvUrl.startsWith('http')) return cvUrl;

    // Ensure no double-slash between base and path
    const base = environment.apiCvPath.endsWith('/')
      ? environment.apiCvPath.slice(0, -1)
      : environment.apiCvPath;

    // cvUrl already starts with '/' (e.g. /assets/job-applications/...)
    return base + cvUrl;
  }

  private loadCvBlob(cvUrl: string): void {
    this.isCvLoading = true;
    this.cvError = false;

    const token = localStorage.getItem('token');
    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`,
    });

    const fullUrl = this.buildFullCvUrl(cvUrl);

    this.http.get(fullUrl, { headers, responseType: 'blob' }).subscribe({
      next: (blob: Blob) => {
        const objectUrl = URL.createObjectURL(blob);
        this.cvBlobUrl =
          this.sanitizer.bypassSecurityTrustResourceUrl(objectUrl);
        this.isCvLoading = false;
      },
      error: (err) => {
        console.error('Failed to load CV:', err);
        this.cvError = true;
        this.isCvLoading = false;
      },
    });
  }

  getCvUrl(cvPath: string): string {
    return this.buildFullCvUrl(cvPath);
  }

  getCvFileName(): string {
    if (!this.applicationDetails?.cvUrl) return 'CV';
    const parts = this.applicationDetails.cvUrl.split('/');
    const raw = decodeURIComponent(parts[parts.length - 1]);
    return (
      raw
        .replace(/^[a-f0-9]{8}-[a-f0-9-]{4,}-[a-f0-9-]+_?/i, '')
        .replace(/^\d+_?/, '') || raw
    );
  }

  formatDate(dateStr: string): string {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  }

  getTimeAgo(dateStr: string): string {
    if (!dateStr) return '';
    const now = new Date();
    const applied = new Date(dateStr);
    const diffMs = now.getTime() - applied.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return 'today';
    if (diffDays === 1) return '1 day';
    if (diffDays < 30) return `${diffDays} days`;
    const diffMonths = Math.floor(diffDays / 30);
    if (diffMonths === 1) return '1 month';
    return `${diffMonths} months`;
  }

  getStatusBadgeClass(status: string): string {
    switch (status?.toUpperCase()) {
      case 'SUBMITTED':
      case 'PENDING':
        return 'bg-blue-100 text-blue-700';
      case 'UNDER_REVIEW':
        return 'bg-yellow-100 text-yellow-700';
      case 'SHORTLISTED':
      case 'ACCEPTED':
      case 'SELECTED_FOR_INTERVIEW':
        return 'bg-green-100 text-green-700';
      case 'REJECTED':
        return 'bg-red-100 text-red-700';
      default:
        return 'bg-gray-100 text-gray-600';
    }
  }

  onSaveNote(): void {
    if (!this.hrNote.trim()) {
      this.showToastNotification('Please add a note before saving', 'error');
      return;
    }

    this.updateStatus(this.applicationDetails.status, true);
  }

  // ─── Fixed: download using blob + auth token ───
  onDownloadCV(): void {
    if (!this.applicationDetails?.cvUrl) return;

    const token = localStorage.getItem('token');
    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`,
    });

    const fullUrl = this.buildFullCvUrl(this.applicationDetails.cvUrl);

    this.http.get(fullUrl, { headers, responseType: 'blob' }).subscribe({
      next: (blob: Blob) => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = this.getCvFileName();
        link.click();
        URL.revokeObjectURL(url);
        this.sendGoogleEvent('download cv');
      },
      error: (err) => {
        console.error('Download failed:', err);
        this.showToastNotification(
          'Failed to download CV. Please try again.',
          'error',
        );
      },
    });
  }

  downloadFile(response: any, filename: string, type: string) {
    if (typeof document !== 'undefined' && typeof window !== 'undefined') {
      const base64Data = response;
      const binaryData = this.base64ToArrayBuffer(base64Data);
      const blob = new Blob([binaryData], { type });
      const link = document.createElement('a');
      const url = window.URL.createObjectURL(blob);

      link.href = url;
      link.download = filename;
      link.click();

      window.URL.revokeObjectURL(url);
      this.sendGoogleEvent('download cv');
    }
  }

  private base64ToArrayBuffer(base64: string): ArrayBuffer {
    let binaryString = '';
    if (typeof window !== 'undefined') {
      binaryString = window.atob(base64);
    }

    const len = binaryString.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes.buffer;
  }

  sendGoogleEvent(name: string) {
    this.googleAnalyticsService.event(name);
    this.gtmService.pushTag({
      event: 'customEvent',
      category: 'button',
      action: 'click',
      label: name,
    });
  }

  onViewProfile(): void {
    const userId = this.applicationDetails?.userId;

    this.router.navigate(['/user-profile'], {
      queryParams: {
        userId: userId,
      },
    });
  }

  private showToastNotification(
    message: string,
    type: 'success' | 'error',
  ): void {
    this.toastMessage = message;
    this.toastType = type;
    this.showToast = true;

    // Auto-hide toast after 4 seconds
    setTimeout(() => {
      this.showToast = false;
    }, 4000);
  }

  private updateStatus(status: string, savingNote = false): void {
    if (!this.applicationId || this.isUpdatingStatus) return;

    this.isUpdatingStatus = true;
    this.updatingStatus = savingNote ? null : status;
    this.isSavingNote = savingNote;

    // Create the payload - only include notes if it has a value
    const payload: { status: string; notes?: string } = {
      status: status,
    };

    // Only add notes if it's not empty/whitespace
    if (this.hrNote && this.hrNote.trim().length > 0) {
      payload.notes = this.hrNote;
    }

    this.applicationService
      .updateApplicationStatus(payload, this.applicationId)
      .subscribe({
        next: () => {
          this.applicationDetails.status = status;
          if (savingNote) {
            this.savedNote = this.hrNote;
          }
          this.finishStatusUpdate();
          const message = savingNote
            ? 'Notes saved successfully!'
            : `Application status updated to ${status.replace(/_/g, ' ').toLowerCase()}.`;
          this.showToastNotification(message, 'success');
        },
        error: (err) => {
          console.error('Failed to update status:', err);
          this.finishStatusUpdate();
          this.showToastNotification('Failed to update status. Please try again.', 'error');
        },
      });
  }

  private finishStatusUpdate(): void {
    this.isUpdatingStatus = false;
    this.updatingStatus = null;
    this.isSavingNote = false;
  }
}
